import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { useCart } from "@/contexts/CartContext";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

export default function CartPage() {
  const { cart, updateLine, removeLine, checkout } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openCheckout, setOpenCheckout] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    address_line: "",
    city: "",
    country: "",
    postal_code: "",
    payment_method: "card",
  });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const order = await checkout(form);
      toast.success("Order confirmed.");
      navigate(`/checkout/success?order=${order.order_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="cart-page" className="bg-[#ffffff] min-h-screen">
      <section className="border-b border-[#000000]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
          <p className="overline">Your bag</p>
          <h1 className="font-serif text-4xl md:text-5xl mt-3">
            {cart.item_count > 0 ? `${cart.item_count} piece${cart.item_count > 1 ? "s" : ""} selected` : "Your bag is quiet."}
          </h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-6">
          {cart.lines.length === 0 ? (
            <div
              data-testid="empty-cart"
              className="border border-[#000000]/10 p-12 text-center"
            >
              <p className="text-[#000000]/70">
                Nothing here yet. Step into the atelier.
              </p>
              <Link to="/shop" className="btn-primary mt-6">
                Browse jackets
              </Link>
            </div>
          ) : (
            cart.lines.map((l) => (
              <article
                key={l.line_id}
                data-testid={`cart-line-${l.line_id}`}
                className="grid grid-cols-[120px_1fr] sm:grid-cols-[160px_1fr_auto] gap-5 sm:gap-8 items-start border-b border-[#000000]/10 pb-6"
              >
                <div className="bg-[#f5f5f5] aspect-[3/4] overflow-hidden">
                  {l.image && (
                    <img
                      src={l.image}
                      alt={l.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <h3 className="font-serif text-lg">{l.title}</h3>
                  <p className="text-xs text-[#000000]/55">
                    {l.size ? `Size ${l.size}` : ""}{" "}
                    {l.is_preorder && (
                      <span className="ml-2 inline-block text-[10px] tracking-[0.18em] uppercase text-[#000000] border border-[#000000]/50 px-2 py-[2px]">
                        Pre-order · 14 days
                      </span>
                    )}
                  </p>
                  {l.body_type && (
                    <p
                      data-testid={`cart-line-body-type-${l.line_id}`}
                      className="text-[11px] tracking-[0.18em] uppercase text-[#000000]/70"
                    >
                      Body Type:{" "}
                      <span className="text-[#c9a84c]">
                        {formatBodyType(l.body_type)}
                      </span>
                    </p>
                  )}
                  <div className="flex items-center gap-3 pt-2">
                    <button
                      data-testid={`qty-decrease-${l.line_id}`}
                      onClick={() => updateLine(l.line_id, l.quantity - 1)}
                      className="w-8 h-8 flex items-center justify-center border border-[#000000]/20 hover:border-[#000000]"
                      aria-label="Decrease"
                    >
                      <Minus size={14} />
                    </button>
                    <span data-testid={`qty-value-${l.line_id}`} className="text-sm w-6 text-center">
                      {l.quantity}
                    </span>
                    <button
                      data-testid={`qty-increase-${l.line_id}`}
                      onClick={() => updateLine(l.line_id, l.quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center border border-[#000000]/20 hover:border-[#000000]"
                      aria-label="Increase"
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      data-testid={`remove-line-${l.line_id}`}
                      onClick={() => removeLine(l.line_id)}
                      className="ml-3 text-[#000000]/50 hover:text-[#000000]"
                      aria-label="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="text-right sm:text-left">
                  <p className="font-serif text-lg text-[#000000]">
                    ${(l.price * l.quantity).toFixed(0)}
                  </p>
                </div>
              </article>
            ))
          )}
        </div>

        {cart.lines.length > 0 && (
          <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start bg-[#f5f5f5] p-8 md:p-10">
            <p className="overline">Summary</p>
            <h3 className="font-serif text-2xl mt-2">Order total</h3>
            <dl className="mt-6 space-y-3 text-sm">
              <Row label="Subtotal" value={`$${cart.totals.subtotal.toFixed(2)}`} />
              <Row label="Shipping" value={cart.totals.shipping === 0 ? "Complimentary" : `$${cart.totals.shipping.toFixed(2)}`} />
              <Row label="Tax (est.)" value={`$${cart.totals.tax.toFixed(2)}`} />
            </dl>
            <div className="hairline-divider my-6" />
            <div className="flex items-baseline justify-between">
              <span className="overline">Total</span>
              <span data-testid="cart-total" className="font-serif text-3xl text-[#000000]">
                ${cart.totals.total.toFixed(2)}
              </span>
            </div>

            {!openCheckout ? (
              <button
                data-testid="open-checkout-button"
                onClick={() => navigate("/checkout")}
                className="btn-primary w-full mt-8"
              >
                Checkout
              </button>
            ) : null}
          </aside>
        )}
      </section>

      <Toaster theme="dark" position="top-center" />
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-[#000000]/70">
    <dt>{label}</dt>
    <dd>{value}</dd>
  </div>
);

// Convert body_type slug (e.g. "broad-shoulder") to a readable label.
const formatBodyType = (slug) =>
  slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
